import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Logger } from '@app/core';
import { Events } from '@app/events/events/events.model';
import { SaleGuestNamesFormComponent } from '@app/sales/sales/sale-guest-names-form/sale-guest-names-form.component';
import { SaleRequest } from '@app/sales/sales/sale-request.model';
import { Sale } from '@app/sales/sales/sale.model';
import { SalesService } from '@app/sales/sales/sales.service';
import { environment } from '@env/environment';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash';
import { NgScrollbar } from 'ngx-scrollbar';

const log = new Logger('SalesRequestsFormComponent');

@Component({
  selector: 'app-sales-requests-form',
  templateUrl: './sales-requests-form.component.html',
  styleUrls: ['./sales-requests-form.component.scss'],
})
export class SalesRequestsFormComponent implements OnInit, AfterViewInit {
  @Input() sale: Sale = null;
  @ViewChild('requestsScrollbar') private requestsScrollbar: NgScrollbar;

  form: FormGroup;
  massActionsForm: FormGroup;
  env = environment;
  checkAll = false;
  noteDefaultColor = '#646774';

  locationOptions = [
    { value: 'city_center', label: 'SALES.SALES_REQUESTS_FORM.LOCATION_CITY_CENTER' },
    { value: 'fairground', label: 'SALES.SALES_REQUESTS_FORM.LOCATION_FAIRGROUND' },
    { value: 'airport', label: 'SALES.SALES_REQUESTS_FORM.LOCATION_AIRPORT' },
    { value: 'free_text', label: 'SALES.SALES_REQUESTS_FORM.FREE_TEXT' },
  ];

  budgetOptions = [
    { value: 'up_to_150', label: 'SALES.SALES_REQUESTS_FORM.BUDGET_UP_TO_150' },
    { value: '150_250', label: 'SALES.SALES_REQUESTS_FORM.BUDGET_150_250' },
    { value: '250_300', label: 'SALES.SALES_REQUESTS_FORM.BUDGET_250_300' },
    { value: 'free_text', label: 'SALES.SALES_REQUESTS_FORM.FREE_TEXT' },
  ];

  constructor(
    private salesService: SalesService,
    private modalService: NgbModal,
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    log.debug(this.sale);
    this.createMassActionsForm();
    this.createForm();
    this.patchForm();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.requestsScrollbar?.update(), 0);
    setTimeout(() => this.requestsScrollbar?.update(), 300);
  }

  get requests(): FormArray {
    return this.form.get('requests') as FormArray;
  }

  get checkedRequestsCount(): number {
    return this.getCheckedRequests().length;
  }

  save(): void {
    if (this.form.invalid || !this.requests.length) {
      this.form.markAllAsTouched();
      return;
    }

    const requests = this.requests.getRawValue().map((request: any) => {
      const payload = cloneDeep(request);

      delete payload.metaFrontCheckBox;
      delete payload.hotel;
      delete payload.meal_plan;
      delete payload.type;
      delete payload.status;

      return payload;
    });

    const payload = { requests };
    log.debug('sale requests payload', payload);

    this.salesService.requests(this.sale.id, payload).subscribe((res: any) => this.modal.close(res));
  }

  addRequest(): void {
    this.requests.push(this.getEmptyRequest(this.sale?.event || null));
    this.requests.markAsDirty();
    this.updateScrollbar();
  }

  removeRequest(index: number): void {
    this.requests.removeAt(index);
    this.requests.markAsDirty();
    this.checkAll = this.requests.length > 0 && this.getCheckedRequests().length === this.requests.length;
    this.syncMassActionsFromCheckedRequests();
    this.updateScrollbar();
  }

  duplicateRequest(request: FormGroup): void {
    const requestValue = cloneDeep(request.getRawValue());
    const guestNamesCount = Array.isArray(requestValue.guest_names) ? requestValue.guest_names.length : 0;
    const duplicate = this.getEmptyRequest(null, guestNamesCount);

    duplicate.patchValue({ ...requestValue, id: null, metaFrontCheckBox: false });
    duplicate.setControl(
      'guest_names',
      this.createGuestNamesFormArray(requestValue.guest_names || [], guestNamesCount)
    );
    this.requests.push(duplicate);
    this.requests.markAsDirty();
    this.updateScrollbar();
  }

  syncGuestNames(index: number): void {
    const request = this.requests.at(index) as FormGroup;
    const requiredNamesCount = Math.max(
      0,
      Number(request.get('people')?.value || 0) * Number(request.get('count')?.value || 0)
    );
    const currentNames = (request.get('guest_names') as FormArray).getRawValue();

    request.setControl('guest_names', this.createGuestNamesFormArray(currentNames, requiredNamesCount));
    request.get('nightly_guest_names')?.patchValue([]);
    request.markAsDirty();
    this.requests.markAsDirty();
    this.updateScrollbar();
  }

  canEditGuestNames(request: FormGroup): boolean {
    return (
      !!request.get('check_in')?.value &&
      !!request.get('check_out')?.value &&
      Number(request.get('people')?.value) > 0 &&
      Number(request.get('count')?.value) > 0
    );
  }

  openGuestNames(request: FormGroup, index: number): void {
    if (!this.canEditGuestNames(request)) {
      return;
    }

    const parentDialog = this.elementRef.nativeElement.closest('.modal-dialog') as HTMLElement;
    const parentWidth = parentDialog?.getBoundingClientRect().width;
    const modalRef = this.modalService.open(SaleGuestNamesFormComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'sale-guest-names-modal',
    });

    if (parentWidth) {
      requestAnimationFrame(() => {
        const dialogs = document.querySelectorAll<HTMLElement>('.sale-guest-names-modal .modal-dialog');
        const dialog = dialogs.item(dialogs.length - 1);

        if (dialog) {
          dialog.style.width = `${parentWidth}px`;
          dialog.style.maxWidth = 'calc(100vw - 24px)';
        }
      });
    }

    modalRef.componentInstance.request = cloneDeep(request.getRawValue());
    modalRef.componentInstance.requestNumber = index + 1;

    modalRef.result.then(
      (result: { guest_names: Array<string | null>; nightly_guest_names: Array<any> }) => {
        if (!result) {
          return;
        }

        request.setControl('guest_names', this.createGuestNamesFormArray(result.guest_names || []));
        request.get('nightly_guest_names')?.patchValue(cloneDeep(result.nightly_guest_names || []));
        request.markAsDirty();
        this.requests.markAsDirty();
        this.updateScrollbar();
      },
      () => undefined
    );
  }

  splitRequest(index: number): void {
    const request = this.requests.at(index) as FormGroup;
    const requestValue = cloneDeep(request.getRawValue());
    const roomCount = Number(requestValue.count) || 0;
    const people = Number(requestValue.people) || 0;

    if (roomCount <= 1) {
      return;
    }

    const guestNamesByRoom = this.chunkGuestNamesByRoom(requestValue.guest_names || [], people, roomCount);
    const nightlyGuestNamesByRoom = this.chunkNightlyGuestNamesByRoom(
      requestValue.nightly_guest_names || [],
      people,
      roomCount
    );

    request.patchValue({
      count: 1,
      metaFrontCheckBox: false,
      nightly_guest_names: cloneDeep(nightlyGuestNamesByRoom[0] || []),
    });
    request.setControl('guest_names', this.createGuestNamesFormArray(guestNamesByRoom[0] || []));
    request.markAsDirty();

    for (let roomIndex = 1; roomIndex < roomCount; roomIndex++) {
      const splitRequest = this.getEmptyRequest(null, people);
      splitRequest.patchValue({
        ...requestValue,
        id: null,
        count: 1,
        metaFrontCheckBox: false,
        nightly_guest_names: cloneDeep(nightlyGuestNamesByRoom[roomIndex] || []),
      });
      splitRequest.setControl('guest_names', this.createGuestNamesFormArray(guestNamesByRoom[roomIndex] || []));
      this.requests.insert(index + roomIndex, splitRequest);
    }

    this.requests.markAsDirty();
    this.updateScrollbar();
  }

  toggleCheckAll(): void {
    this.checkAll = !this.checkAll;
    this.requests.controls.forEach((control) => control.get('metaFrontCheckBox')?.patchValue(this.checkAll));
    this.syncMassActionsFromCheckedRequests();
  }

  applyToChecked(): void {
    const checkedRequests = this.getCheckedRequests();

    if (!checkedRequests.length) {
      return;
    }

    const sharedFields = this.getSharedFieldsValue();
    checkedRequests.forEach((request: FormGroup) => {
      request.patchValue({
        location_options: cloneDeep(sharedFields.location_options),
        custom_location: sharedFields.custom_location,
        meal_plan_id: sharedFields.meal_plan_id,
        hotel_stars: sharedFields.hotel_stars,
        hotel_text: sharedFields.hotel_text,
        budget_options: cloneDeep(sharedFields.budget_options),
        custom_budget: sharedFields.custom_budget,
        budget: this.buildLegacyBudget(sharedFields.budget_options, sharedFields.custom_budget),
        status_id: sharedFields.status_id,
        hotel_id: null,
        hotel: null,
      });
      request.markAsDirty();
    });
    this.requests.markAsDirty();
  }

  hasSharedOption(controlName: 'location_options' | 'budget_options', optionValue: string): boolean {
    const options = this.massActionsForm.get(controlName)?.value;
    return Array.isArray(options) && options.includes(optionValue);
  }

  syncMassActionsFromCheckedRequests(): void {
    const checkedRequests = this.getCheckedRequests();
    this.checkAll = this.requests.length > 0 && checkedRequests.length === this.requests.length;

    if (!checkedRequests.length) {
      this.massActionsForm.patchValue(
        {
          location_options: [],
          custom_location: null,
          meal_plan_id: null,
          hotel_stars: null,
          hotel_text: null,
          budget_options: [],
          custom_budget: null,
          status_id: null,
        },
        { emitEvent: false }
      );
      return;
    }

    if (checkedRequests.length !== 1) {
      return;
    }

    const selectedRequest = checkedRequests[0];
    this.massActionsForm.patchValue(
      {
        location_options: cloneDeep(selectedRequest.get('location_options')?.value || []),
        custom_location: selectedRequest.get('custom_location')?.value || null,
        meal_plan_id: selectedRequest.get('meal_plan_id')?.value || null,
        hotel_stars: selectedRequest.get('hotel_stars')?.value || null,
        hotel_text: selectedRequest.get('hotel_text')?.value || null,
        budget_options: cloneDeep(selectedRequest.get('budget_options')?.value || []),
        custom_budget: selectedRequest.get('custom_budget')?.value || null,
        status_id: selectedRequest.get('status_id')?.value || null,
      },
      { emitEvent: false }
    );
  }

  moveRequestTo(fromIndex: number, rawValue: number | string): void {
    const position = Number(rawValue);

    if (!Number.isFinite(position)) {
      return;
    }

    const toIndex = Math.max(0, Math.min(this.requests.length - 1, Math.round(position) - 1));
    if (toIndex !== fromIndex) {
      this.moveRequest(fromIndex, toIndex);
    }
  }

  moveUp(index: number): void {
    this.moveRequest(index, index - 1);
  }

  moveDown(index: number): void {
    this.moveRequest(index, index + 1);
  }

  isNoteExpanded(request: FormGroup): boolean {
    return !!this.getRequestMetaFront(request).noteExpanded;
  }

  toggleNoteExpanded(request: FormGroup): void {
    const metaFront = this.getRequestMetaFront(request);
    metaFront.noteExpanded = !metaFront.noteExpanded;
    this.updateScrollbar();
  }

  setRequestNoteColor(request: FormGroup, color: string): void {
    request.get('note_color')?.patchValue(color || null);
    request.markAsDirty();
    this.requests.markAsDirty();
  }

  clearRequestNoteColor(request: FormGroup): void {
    request.get('note_color')?.patchValue(null);
    request.markAsDirty();
    this.requests.markAsDirty();
  }

  private moveRequest(fromIndex: number, toIndex: number): void {
    if (fromIndex < 0 || fromIndex >= this.requests.length || toIndex < 0 || toIndex >= this.requests.length) {
      return;
    }

    const request = this.requests.at(fromIndex);
    this.requests.removeAt(fromIndex);
    this.requests.insert(toIndex, request);
    this.requests.markAsDirty();
    this.updateScrollbar();
  }

  private createMassActionsForm(): void {
    this.massActionsForm = this.formBuilder.group({
      location_options: [[]],
      custom_location: [null],
      meal_plan_id: [null],
      hotel_stars: [null],
      hotel_text: [null],
      budget_options: [[]],
      custom_budget: [null],
      status_id: [null],
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({ id: [null], requests: new FormArray([]) });
  }

  private getEmptyRequest(event: Events = null, guestNamesCount: number = 0): FormGroup {
    const request = new FormGroup({
      id: new FormControl(null),
      budget: new FormControl(null),
      budget_options: new FormControl([]),
      check_in: new FormControl(event?.date_from || null, [Validators.required]),
      check_out: new FormControl(event?.date_to || null, [Validators.required]),
      count: new FormControl(null, [Validators.required, Validators.min(1)]),
      custom_budget: new FormControl(null),
      custom_location: new FormControl(null),
      hotel_id: new FormControl(null),
      hotel: new FormControl(null),
      hotel_text: new FormControl(null),
      hotel_stars: new FormControl(null),
      location_options: new FormControl([]),
      meal_plan_id: new FormControl(null),
      meal_plan: new FormControl({ value: null, disabled: true }),
      nightly_guest_names: new FormControl([]),
      note: new FormControl(null),
      note_color: new FormControl(null),
      type_id: new FormControl(null, [Validators.required]),
      type: new FormControl({ value: null, disabled: true }),
      status_id: new FormControl(null, [Validators.required]),
      status: new FormControl({ value: null, disabled: true }),
      people: new FormControl(null, [Validators.min(1)]),
      guest_names: this.createGuestNamesFormArray([], guestNamesCount),
      metaFrontCheckBox: new FormControl(false),
    });

    this.getRequestMetaFront(request);
    return request;
  }

  private createMany(requests: Array<SaleRequest>): FormArray {
    const formArray = new FormArray([]);
    requests.forEach((request: SaleRequest) =>
      formArray.push(this.getEmptyRequest(null, (request.people || 0) * (request.count || 0)))
    );
    return formArray;
  }

  private patchForm(): void {
    if (this.sale?.requests?.length) {
      this.form.setControl('requests', this.createMany(this.sale.requests));
      setTimeout(() => {
        this.form.patchValue(this.sale);
        this.updateScrollbar();
      });
      return;
    }

    this.requests.push(this.getEmptyRequest(this.sale?.event || null));
    this.updateScrollbar();
  }

  private getCheckedRequests(): Array<FormGroup> {
    return this.requests.controls.filter((control) => !!control.get('metaFrontCheckBox')?.value) as Array<FormGroup>;
  }

  private getSharedFieldsValue(): {
    location_options: Array<string>;
    custom_location: string | null;
    meal_plan_id: number | null;
    hotel_stars: number | null;
    hotel_text: string | null;
    budget_options: Array<string>;
    custom_budget: string | null;
    status_id: number | null;
  } {
    const locationOptions = cloneDeep(this.massActionsForm.get('location_options')?.value || []);
    const budgetOptions = cloneDeep(this.massActionsForm.get('budget_options')?.value || []);

    return {
      location_options: locationOptions,
      custom_location: locationOptions.includes('free_text')
        ? this.massActionsForm.get('custom_location')?.value || null
        : null,
      meal_plan_id: this.massActionsForm.get('meal_plan_id')?.value || null,
      hotel_stars: this.massActionsForm.get('hotel_stars')?.value || null,
      hotel_text: this.massActionsForm.get('hotel_text')?.value || null,
      budget_options: budgetOptions,
      custom_budget: budgetOptions.includes('free_text')
        ? this.massActionsForm.get('custom_budget')?.value || null
        : null,
      status_id: this.massActionsForm.get('status_id')?.value || null,
    };
  }

  private buildLegacyBudget(budgetOptions: Array<string>, customBudget: string | null): string | null {
    const labels: { [key: string]: string } = {
      up_to_150: 'Up to €150',
      '150_250': '€150–€250',
      '250_300': '€250–€300',
      free_text: 'Free-text',
    };
    const values = budgetOptions.filter((option: string) => !!labels[option]).map((option: string) => labels[option]);

    if (customBudget) {
      values.push(customBudget);
    }

    return values.length ? values.join(', ') : null;
  }

  private createGuestNamesFormArray(guestNames: Array<string | null> = [], fallbackCount: number = null): FormArray {
    const formArray = new FormArray([]);
    const count = fallbackCount === null ? guestNames.length : fallbackCount;

    for (let i = 0; i < count; i++) {
      formArray.push(new FormControl(guestNames[i] ?? null));
    }

    return formArray;
  }

  private chunkGuestNamesByRoom(guestNames: Array<string>, people: number, roomCount: number): Array<Array<string>> {
    return Array.from({ length: roomCount }, (_, roomIndex: number) =>
      guestNames.slice(roomIndex * people, (roomIndex + 1) * people)
    );
  }

  private chunkNightlyGuestNamesByRoom(
    nightlyGuestNames: Array<any>,
    people: number,
    roomCount: number
  ): Array<Array<any>> {
    const chunks: Array<Array<any>> = Array.from({ length: roomCount }, () => []);

    nightlyGuestNames.forEach((night: any) => {
      const roomSlots = Array.isArray(night?.room_slots) ? night.room_slots : [];
      for (let roomIndex = 0; roomIndex < roomCount; roomIndex++) {
        const roomSlot = cloneDeep(roomSlots[roomIndex] || {});
        roomSlot.guest_names = Array.isArray(roomSlot.guest_names) ? roomSlot.guest_names.slice(0, people) : [];
        chunks[roomIndex].push({ date: night?.date || null, room_slots: [roomSlot] });
      }
    });

    return chunks;
  }

  private getRequestMetaFront(request: FormGroup): { noteExpanded: boolean } {
    if (!(request as any).metaFront) {
      (request as any).metaFront = { noteExpanded: false };
    }
    return (request as any).metaFront;
  }

  private updateScrollbar(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => this.requestsScrollbar?.update()));
  }
}
